import { memo } from 'react';
import classes from 'classnames';
import { useTranslation } from 'react-i18next';
import { dateFormat, numberToCurrency, numberToPercent } from 'utils/translate';
import { Loan } from 'hooks/webapi/application/useActiveLoanQuery';
import Placeholder from '../Placeholder/Placeholder';
import styles from './Offer.module.scss';

export type Props = {
  loan?: Loan;
  loading?: boolean;
  className?: string;
  showInterest?: boolean;
};

const ActiveLoan = memo(function ActiveLoan({
  loan,
  showInterest = true,
  loading,
  className,
}: Props) {
  const { t } = useTranslation(['upsale', 'calculator']);
  return (
    <section
      className={classes(styles['offer-wrapper'], className)}
      data-test="activeLoan"
    >
      <hr className={styles.hr} data-test="activeLoan-separator-principal" />
      <div className={styles.row} data-test="activeLoan-principal">
        <div data-test="activeLoan-principal-text">{t('table.loan')}</div>
        <div data-test="offer-new-principal">
          <Placeholder loading={loading}>
            {numberToCurrency(loan?.principalAmount)}
          </Placeholder>
        </div>
      </div>
      <hr className={styles.hr} data-test="activeLoan-separator-offerMtp" />
      <div className={styles.row} data-test="activeLoan-offerMtp">
        <div
          className={styles['bold-info']}
          data-test="activeLoan-offerMtp-text"
        >
          {t('calculator:offer.mtp')}
        </div>
        <div className={styles['bold-info']}>
          <Placeholder dataTest="offer-mtp" loading={loading}>
            <span
              className={styles.larger}
              data-test="activeLoan-offerMtp-number"
            >
              {numberToCurrency(loan?.loanExtensionFees[0]?.fee)}
            </span>
          </Placeholder>
        </div>
      </div>
      <hr
        className={styles.hr}
        data-test="activeLoan-separator-futureExtensionFee"
      />
      <div className={styles.row} data-test="activeLoan-futureExtensionFee">
        <div data-test="activeLoan-futureExtensionFee-text">
          {t('table.futureExtensionFees')}
        </div>
        <div data-test="offer-new-principal">
          <Placeholder
            loading={loading}
            dataTest="activeLoan-futureExtensionFee-amount"
          >
            {numberToCurrency(loan?.futureExtensionFees[0].fee)}
          </Placeholder>
        </div>
      </div>
      <hr className={styles.hr} data-test="activeLoan-separator-dueDate" />
      <div className={styles.row} data-test="activeLoan-dueDate">
        <div
          className={styles.lineHeightRow}
          data-test="activeLoan-dueDate-text"
        >
          {t('table.due_date')}
        </div>
        <div className={styles.lineHeightRowDate}>
          <Placeholder dataTest="offer-agreement-end-date" loading={loading}>
            {dateFormat(loan?.dueDate)}
          </Placeholder>
        </div>
      </div>
      <hr className={styles.hr} data-test="activeLoan-separator-interest" />
      {showInterest && (
        <div className={styles.row} data-test="activeLoan-interest">
          <div data-test="activeLoan-interest-text">{t('table.interest')}</div>
          <div>
            <Placeholder
              dataTest="offer-agreement-interest-rate"
              loading={loading}
            >
              {numberToPercent(loan?.interestRatePerYear)}
            </Placeholder>
          </div>
        </div>
      )}
    </section>
  );
});

export default ActiveLoan;
